import {
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	deferred,
	Deferred,
	delay,
	isNil,
	isNilOrWhiteSpace,
	isNotNil
} from "client/Misc/utils";
import { isAndroid, isIos } from "client_legacy/ndaw/mobile";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { millisecondsInAMinute } from "client/DateAndTime/dateTimeUtils";
import { useStableImperativeCallback } from "./useStableImperativeCallback";
import { useUnmountedCancellation } from "./useUnmountedCancellation";

const SpeechRecognition: (new () => SpeechRecognition) | nil = window.SpeechRecognition
	|| window.webkitSpeechRecognition;

export type SpeechToTextErrorKind =
	| "aborted"
	| "audio-capture"
	| "language-not-supported"
	| "network"
	| "no-speech"
	| "not-allowed"
	| "service-not-allowed"
	| "unknown";

export type SpeechToTextResult = {
	isSupported: false;
} | {
	isSupported: true;
	isListening: boolean;
	interimTrascript: string;
	start: () => void;
	stop: () => Promise<void>;
};

export type SpeechToTextOptions = {
	maxSpeechDurationMinutes?: number;
	culture?: string;
	onResult: (transcript: string) => void;
	onError?: (errorKind: SpeechToTextErrorKind) => void;
};

export function useSpeechToText(options: SpeechToTextOptions): SpeechToTextResult {
	const [isListening, setIsListening] = useState<boolean>(false);
	const [interimText, setInterimText] = useState<string>("");

	const {
		maxSpeechDurationMinutes = 60,
		culture,
		onResult,
		onError
	} = options;

	const maxDurationCancellationRef: RefObject<SetTimeoutHandle | null> = useRef<SetTimeoutHandle | null>(null);
	const recognitionRef: RefObject<SpeechRecognition | null> = useRef<SpeechRecognition | null>(null);
	const shouldRestartAfterEndRef: RefObject<boolean> = useRef<boolean>(false);
	const pendingStopCompletionRef: RefObject<Deferred<void> | null> = useRef<Deferred<void> | null>(null);
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const startListening = useCallback(
		(): void => {
			if (!recognitionRef.current) {
				throw new Error("Speech recognition is not set");
			}

			recognitionRef.current
				.start();

			if (maxDurationCancellationRef.current) {
				clearTimeout(maxDurationCancellationRef.current);
			}

			const durationMs: number = maxSpeechDurationMinutes * millisecondsInAMinute;

			maxDurationCancellationRef.current = setTimeout(
				(): void => {
					recognitionRef
						.current
						?.stop();
				},
				durationMs
			);
		},
		[maxSpeechDurationMinutes]
	);

	const stopCompleteHandler = useCallback(
		(): void => {
			pendingStopCompletionRef
				.current
				?.resolve();

			pendingStopCompletionRef.current = null;
		},
		[]
	);

	const endHandler = useCallback(
		async (): Promise<void> => {
			setInterimText("");

			// On iOS end event envokes before
			// the last result so we wait for it
			if (isIos()) {
				await delay(200, globalCancellation);
				stopCompleteHandler();
			} else {
				stopCompleteHandler();
			}

			if (shouldRestartAfterEndRef.current) {
				startListening();
			} else {
				setIsListening(false);
			}
		},
		[
			startListening,
			stopCompleteHandler,
			globalCancellation
		]
	);

	const errorHandler = useCallback(
		(error: SpeechRecognitionErrorEvent): void => {
			setIsListening(false);

			if (isAndroid()
				&& error.error !== "not-allowed"
				&& error.error !== "aborted"
			) {
				return;
			}

			shouldRestartAfterEndRef.current = false;

			if (onError) {
				const errorkind: SpeechToTextErrorKind = convertToSpeechToTextErrorKind(error.error);
				onError(errorkind);
			} else {
				console.error("Speech recognition error:", error);
			}
		},
		[onError]
	);

	const resultHandler = useCallback(
		(event: SpeechRecognitionEvent) => {
			const {
				results,
				resultIndex
			} = event;

			let interimText: string = "";

			for (let i = resultIndex; i < results.length; i++) {
				const result: SpeechRecognitionResult = results[i];
				const resultAlternative: SpeechRecognitionAlternative | undefined = result[0];
				const text: string = resultAlternative
					.transcript
					.trim();

				if (isNilOrWhiteSpace(text)) {
					continue;
				}

				if (result.isFinal
					&& (!isAndroid() || resultAlternative.confidence > 0)
				) {
					onResult(text);
				} else {
					interimText = concatTextParts([interimText, text]);
				}
			}

			setInterimText(interimText);
		},
		[onResult]
	);

	const setSpeechRecognition = useStableImperativeCallback(
		(): void => {
			if (!SpeechRecognition) {
				throw new Error("Speech recognition is not supported");
			}

			const recognition = new SpeechRecognition();
			recognition.continuous = true;
			recognition.interimResults = true;
			recognition.maxAlternatives = 1;
			recognition.onend = endHandler;
			recognition.onerror = errorHandler;
			recognition.onresult = resultHandler;

			if (culture) {
				recognition.lang = culture;
			}

			shouldRestartAfterEndRef.current = isAndroid();
			recognitionRef.current = recognition;
		}
	);

	const resetHandler = useCallback(
		(): void => {
			if (recognitionRef.current) {
				recognitionRef.current.abort();
				recognitionRef.current = null;
			}

			setSpeechRecognition();
		},
		[setSpeechRecognition]
	);

	const startHandler = useCallback(
		(): void => {
			if (isListening) {
				return;
			}

			setIsListening(true);
			resetHandler();
			startListening();
		},
		[
			isListening,
			startListening,
			resetHandler
		]
	);

	const stopHandler = useCallback(
		async (): Promise<void> => {
			if (!isListening || isNotNil(pendingStopCompletionRef.current)) {
				return;
			}

			if (!recognitionRef.current) {
				throw new Error("Speech recognition is not started but stop is invoked");
			}

			shouldRestartAfterEndRef.current = false;
			const completion: Deferred<void> = deferred<void>();
			pendingStopCompletionRef.current = completion;

			recognitionRef
				.current
				.stop();

			await completion.promise;
			setIsListening(false);
		},
		[isListening]
	);

	useEffect(
		(): void => {
			hasImplicitDependencyOn(
				options.culture,
				options.maxSpeechDurationMinutes
			);

			if (recognitionRef.current) {
				recognitionRef.current.abort();
				recognitionRef.current = null;
			}
		},
		[
			options.culture,
			options.maxSpeechDurationMinutes
		]
	);

	useEffect(
		(): (() => void) | void => {
			return (): void => {
				shouldRestartAfterEndRef.current = false;

				recognitionRef
					.current
					?.abort();

				if (maxDurationCancellationRef.current) {
					clearTimeout(maxDurationCancellationRef.current);
				}
			};
		},
		[]
	);

	return useMemo(
		(): SpeechToTextResult => {
			return isNil(SpeechRecognition)
				? { isSupported: false}
				: {
					isSupported: true,
					isListening,
					interimTrascript: interimText,
					start: startHandler,
					stop: stopHandler
				};
		},
		[
			isListening,
			interimText,
			startHandler,
			stopHandler
		]
	);
}

function convertToSpeechToTextErrorKind(
	error: SpeechRecognitionErrorCode
): SpeechToTextErrorKind {
	switch (error) {
		case "aborted":
			return "aborted";
		case "audio-capture":
			return "audio-capture";
		case "language-not-supported":
			return "language-not-supported";
		case "network":
			return "network";
		case "no-speech":
			return "no-speech";
		case "not-allowed":
			return "not-allowed";
		case "service-not-allowed":
			return "service-not-allowed";
		default:
			return "unknown";
	}
}

export function concatTextParts(textParts: readonly string[]) {
	return textParts
		.map(x => x.trim())
		.join(" ")
		.trim();
}